import { useCallback, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  BookOpen,
  ChevronRight,
  CreditCard,
  Eye,
  GripVertical,
  LayoutDashboard,
  Medal,
  PanelLeftClose,
  PanelLeftOpen,
  Receipt,
  ScrollText,
  Sparkles,
  Trophy,
  Users,
  AlertCircle,
  LogOut,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../components/core/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/core/Dialog';
import { ConfirmDialog } from '../components/core/ConfirmDialog';
import { A4Paper } from '../components/pdf/A4Paper';
import { WizardEmbed } from './wizard/WizardPage';
import { GeneratedQuestionsEmbed } from './GeneratedQuestionsPage';
import type { GeneratedExamPaper } from '../types/generated';
import { useDashboardData } from '../hooks/useDashboardData';
import { useRenewalReminder } from '../hooks/useRenewalReminder';
import { RenewalReminderBanner } from '../components/subscription/RenewalReminderBanner';
import type { HistoryEntry } from '../types/dashboard';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { LanguageToggle } from '../components/core/LanguageToggle';
import { DEPARTMENT_NAME_KEYS, PLAN_NAME_KEYS } from '../i18n';
import type { TranslateFn, TranslationKey } from '../i18n';
import { cancelSubscription } from '../api/subscription.api';
import type { UserSubscription, PaymentRecord } from '../api/subscription.api';
import type { Department } from '../types/wizard';
import {
  DEFAULT_DASHBOARD_TAB,
  getDashboardTabPath,
  isAdminTab,
  resolveDashboardTab,
} from '../constants/dashboard';
import type { DashboardTab as Tab } from '../constants/dashboard';
import { PRICING_PATH } from '../constants/routes';
import { AdminUsersTab } from '../components/admin/AdminUsersTab';
import { AdminActivityTab } from '../components/admin/AdminActivityTab';
import { AdminStatsTab } from '../components/admin/AdminStatsTab';

interface NavGroup {
  labelKey: TranslationKey;
  tabs: { id: Tab; labelKey: TranslationKey; icon: React.ComponentType<{ className?: string }> }[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    labelKey: 'nav.analytics',
    tabs: [{ id: 'overview', labelKey: 'nav.overview', icon: LayoutDashboard }],
  },
  {
    labelKey: 'nav.content',
    tabs: [{ id: 'history', labelKey: 'nav.history', icon: BookOpen }],
  },
  {
    labelKey: 'nav.account',
    tabs: [
      { id: 'subscription', labelKey: 'nav.subscription', icon: CreditCard },
      { id: 'payments', labelKey: 'nav.payments', icon: Receipt },
      { id: 'leaderboard', labelKey: 'nav.leaderboard', icon: Trophy },
    ],
  },
];

/** Appended to the sidebar only for superadmins. Kept out of NAV_GROUPS so an
 *  ordinary render can never accidentally include it. */
const ADMIN_NAV_GROUP: NavGroup = {
  labelKey: 'nav.admin',
  tabs: [
    { id: 'admin-users', labelKey: 'nav.adminUsers', icon: Users },
    { id: 'admin-activity', labelKey: 'nav.adminActivity', icon: ScrollText },
    { id: 'admin-stats', labelKey: 'nav.adminStats', icon: BarChart3 },
  ],
};

const ALL_TABS = [...NAV_GROUPS, ADMIN_NAV_GROUP].flatMap((group) => group.tabs);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatNumber(n: number): string {
  return n.toLocaleString('en-BD');
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-BD', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function levelLabel(classLevel: number): string {
  return classLevel === 10 ? 'SSC' : 'HSC';
}

// History entries are read back from localStorage, where `department` is a plain
// string that predates this map — so an unrecognised value is shown raw rather
// than resolving to nothing.
function departmentLabel(department: string, t: TranslateFn): string {
  const key = DEPARTMENT_NAME_KEYS[department as Department];
  return key ? t(key) : department;
}

function getPaymentStatusColor(status: PaymentRecord['status']): string {
  if (status === 'completed') return 'bg-emerald-500/20 text-emerald-400';
  if (status === 'pending') return 'bg-amber-500/20 text-amber-400';
  return 'bg-rose-500/20 text-rose-400';
}

function getMedalColor(rank: number): string {
  if (rank === 1) return 'text-amber-500';
  if (rank === 2) return 'text-slate-400';
  if (rank === 3) return 'text-amber-700';
  return 'text-slate-300';
}

// ─── StatRow (compact for panel) ──────────────────────────────────────────────

// ─── QuickStatCard (Sneat-style metric tile) ────────────────────────────────────────

function QuickStatCard({
  icon: Icon,
  label,
  value,
  sub,
  gradient,
}: Readonly<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  sub: string;
  gradient: string;
}>) {
  return (
    <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 shadow-sm">
      <div className="flex items-start justify-between mb-4">
        <p className="text-sm font-semibold text-slate-400 leading-tight">{label}</p>
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br ${gradient} shadow-md flex-shrink-0`}
        >
          <Icon className="h-[18px] w-[18px] text-white" />
        </div>
      </div>
      <p className="text-2xl font-black text-white leading-none">{value}</p>
      <p className="text-xs text-slate-500 mt-1.5">{sub}</p>
    </div>
  );
}

// ─── Panel tab content ───────────────────────────────────────────────────────

interface PanelTabContentProps {
  activeTab: Tab;
  history: HistoryEntry[];
  pagedHistory: HistoryEntry[];
  historyPage: number;
  totalPages: number;
  analyticsRows: {
    subject: string;
    department: string;
    classLevel: number;
    count: number;
    lastGenerated: string;
  }[];
  leaderboard: { rank: number; name: string; totalQuestions: number; isCurrentUser?: boolean }[];
  myRank: number;
  totalQuestions: number;
  sub: UserSubscription | null;
  subLoading: boolean;
  subError: string | null;
  payments: PaymentRecord[];
  daysRemaining: number;
  setHistoryPage: (fn: (p: number) => number) => void;
  setViewingEntry: (e: HistoryEntry) => void;
  setActiveTab: (t: Tab) => void;
  onCancelSuccess: () => void;
  adminUserFilter?: string;
  setAdminUserFilter: (userId: string | undefined) => void;
}

function OverviewTab({
  analyticsRows,
  leaderboard,
  myRank,
  totalQuestions,
  setActiveTab,
}: Readonly<
  Pick<
    PanelTabContentProps,
    'analyticsRows' | 'leaderboard' | 'myRank' | 'totalQuestions' | 'setActiveTab'
  >
>) {
  const { t } = useTranslation();

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-slate-700 bg-slate-800 overflow-hidden">
        <div className="px-3 py-2.5 border-b border-slate-700 flex items-center gap-1.5">
          <BarChart3 className="h-4 w-4 text-violet-400" />
          <span className="text-xs font-semibold text-slate-300">{t('dashboard.bySubject')}</span>
        </div>
        {analyticsRows.length === 0 ? (
          <p className="px-3 py-6 text-center text-xs text-slate-500">
            {t('dashboard.noQuestionsYet')}
          </p>
        ) : (
          <div className="divide-y divide-slate-700">
            {analyticsRows.map((row) => {
              const rowKey = `${row.subject}__${row.department}__${row.classLevel}`;
              return (
                <div key={rowKey} className="px-3 py-2.5 flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white truncate">{row.subject}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {departmentLabel(row.department, t)} · {levelLabel(row.classLevel)}
                    </p>
                  </div>
                  <span className="text-sm font-bold text-violet-400 flex-shrink-0">
                    {formatNumber(row.count)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <button
        type="button"
        className="w-full rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 flex items-center gap-2 text-left"
        onClick={() => setActiveTab('leaderboard')}
      >
        <Medal className="h-5 w-5 text-amber-500 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-amber-300">
            {t('dashboard.rankOf', { rank: myRank, total: leaderboard.length })}
          </p>
          <p className="text-xs text-amber-400 truncate">
            {totalQuestions === 0 ? t('dashboard.rankUp') : t('dashboard.keepGenerating')}
          </p>
        </div>
        <ChevronRight className="h-4 w-4 text-amber-500 flex-shrink-0" />
      </button>
    </div>
  );
}

function HistoryTab({
  pagedHistory,
  historyPage,
  totalPages,
  setHistoryPage,
  setViewingEntry,
}: Readonly<
  Pick<
    PanelTabContentProps,
    'pagedHistory' | 'historyPage' | 'totalPages' | 'setHistoryPage' | 'setViewingEntry'
  >
>) {
  const { t } = useTranslation();

  if (pagedHistory.length === 0) {
    return <p className="py-8 text-center text-xs text-slate-400">{t('dashboard.historyEmpty')}</p>;
  }
  return (
    <div className="space-y-2">
      {pagedHistory.map((entry) => (
        <div
          key={entry.id}
          className="rounded-xl border border-slate-700 bg-slate-800 p-3 space-y-1.5"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-white truncate">{entry.subject}</p>
              <p className="text-xs text-slate-400 truncate">
                {levelLabel(entry.classLevel)} · {entry.examType} · {entry.difficulty}
              </p>
            </div>
            <button
              onClick={() => setViewingEntry(entry)}
              className="flex items-center gap-1 text-xs font-medium text-violet-400 hover:text-violet-300 flex-shrink-0"
            >
              <Eye className="h-3 w-3" />
              {t('common.view')}
            </button>
          </div>
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>{t('dashboard.questionsCount', { count: entry.numberOfQuestions })}</span>
            <span>{formatDate(entry.generatedAt)}</span>
          </div>
        </div>
      ))}
      {totalPages > 1 && (
        <div className="flex justify-between gap-1 pt-1">
          <button
            disabled={historyPage === 1}
            onClick={() => setHistoryPage((p) => p - 1)}
            className="flex-1 py-1.5 rounded-lg border border-slate-700 text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-40"
          >
            {t('common.prev')}
          </button>
          <span className="text-xs text-slate-400 self-center px-2">
            {historyPage}/{totalPages}
          </span>
          <button
            disabled={historyPage === totalPages}
            onClick={() => setHistoryPage((p) => p + 1)}
            className="flex-1 py-1.5 rounded-lg border border-slate-700 text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-40"
          >
            {t('common.next')}
          </button>
        </div>
      )}
    </div>
  );
}

function SubscriptionTab({
  sub,
  loading,
  error,
  onCancelSuccess,
}: Readonly<{
  sub: UserSubscription | null;
  loading: boolean;
  error: string | null;
  onCancelSuccess: () => void;
}>) {
  const { user, signOut } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!user) {
    return (
      <div className="text-center py-6">
        <p className="text-xs text-slate-500 mb-3">{t('subscription.signInPrompt')}</p>
        <button
          onClick={() => navigate('/login')}
          className="text-xs text-indigo-600 font-semibold hover:underline"
        >
          Sign In
        </button>
      </div>
    );
  }

  if (loading) {
    return <div className="text-xs text-slate-400 text-center py-6">{t('common.loading')}</div>;
  }

  if (error || !sub) {
    return (
      <div className="text-xs text-rose-500 text-center py-6">
        {error ?? t('subscription.loadError')}
      </div>
    );
  }

  const daysLeft = Math.max(
    0,
    Math.ceil((new Date(sub.periodEnd).getTime() - Date.now()) / 86400000)
  );
  const pdfPct = Math.min(100, Math.round((sub.pdfsGenerated / sub.limits.pdfsPerMonth) * 100));

  const handleCancel = async () => {
    setCancelling(true);
    setCancelError(null);
    try {
      await cancelSubscription();
      setConfirmOpen(false);
      onCancelSuccess();
    } catch (err: unknown) {
      // Kept on screen inside the dialog — this used to be written to a discarded
      // state slot, so a failed cancellation told the user nothing at all.
      const e = err as { message?: string };
      setCancelError(e.message ?? t('subscription.cancelFailed'));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Current plan card */}
      <div className="rounded-xl border-2 border-violet-500/30 bg-violet-500/10 p-3">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-xs font-bold text-violet-200">
              {t('subscription.planLabel', { plan: t(PLAN_NAME_KEYS[sub.plan]) })}
            </p>
            <p className="text-xs text-violet-400 mt-0.5">{t(`status.${sub.status}`)}</p>
          </div>
          <div className="text-right">
            <p
              className={`text-xs font-bold ${daysLeft <= 5 ? 'text-rose-400' : 'text-violet-300'}`}
            >
              {daysLeft === 0
                ? t('common.expired')
                : t('subscription.daysLeft', { days: daysLeft })}
            </p>
            <p className="text-xs text-violet-400">{formatDate(sub.periodEnd)}</p>
          </div>
        </div>
        {/* Usage bar */}
        <div className="mt-1">
          <div className="flex justify-between text-xs text-violet-400 mb-1">
            <span>{t('subscription.pdfsUsed')}</span>
            <span>
              {sub.pdfsGenerated} / {sub.limits.pdfsPerMonth}
            </span>
          </div>
          <div className="h-1.5 bg-violet-500/20 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                pdfPct >= 90 ? 'bg-rose-500' : pdfPct >= 70 ? 'bg-amber-500' : 'bg-violet-500'
              }`}
              style={{ width: `${pdfPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* Expiry warning */}
      {daysLeft <= 7 && daysLeft > 0 && sub.status === 'active' && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-300">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-amber-400" />
          {daysLeft === 1
            ? t('subscription.expiryWarningOne')
            : t('subscription.expiryWarning', { days: daysLeft })}
        </div>
      )}

      {/* Actions */}
      <button
        onClick={() => navigate(PRICING_PATH)}
        className="flex items-center justify-center gap-1.5 w-full rounded-xl bg-violet-600 py-2 text-xs font-semibold text-white hover:bg-violet-500 transition-colors"
      >
        <ExternalLink className="h-3.5 w-3.5" />
        {t('subscription.viewUpgrade')}
      </button>

      {sub.plan !== 'free_trial' && sub.status === 'active' && (
        <button
          onClick={() => setConfirmOpen(true)}
          disabled={cancelling}
          className="w-full rounded-xl border border-rose-500/40 py-2 text-xs font-semibold text-rose-400 hover:bg-rose-500/10 transition-colors"
        >
          {cancelling ? t('subscription.cancelling') : t('subscription.cancelAction')}
        </button>
      )}

      <ConfirmDialog
        open={confirmOpen}
        // Dismissing clears the error so a retry does not open onto a stale failure.
        onOpenChange={(next) => {
          setConfirmOpen(next);
          if (!next) setCancelError(null);
        }}
        variant="destructive"
        title={t('subscription.confirmTitle')}
        description={t('subscription.confirmBody')}
        confirmLabel={t('subscription.confirmYes')}
        cancelLabel={t('subscription.confirmNo')}
        loading={cancelling}
        error={cancelError}
        onConfirm={handleCancel}
      />

      {/* Per-plan limits */}
      <div className="rounded-lg bg-slate-700/50 border border-slate-700 p-3 space-y-1">
        <p className="text-xs font-semibold text-slate-300 mb-1">{t('subscription.limitsTitle')}</p>
        <p className="text-xs text-slate-400">
          {t('subscription.maxWritten')}{' '}
          <strong className="text-slate-200">{sub.limits.maxWritten}</strong>
        </p>
        <p className="text-xs text-slate-400">
          {t('subscription.maxMcq')} <strong className="text-slate-200">{sub.limits.maxMCQ}</strong>
        </p>
        <p className="text-xs text-slate-400">
          {t('subscription.pdfsRemaining')}{' '}
          <strong className="text-slate-200">{sub.pdfsRemaining}</strong>
        </p>
      </div>

      {/* Sign out */}
      <button
        onClick={() => signOut()}
        className="flex items-center gap-1.5 w-full rounded-xl border border-slate-700 py-2 text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-700 transition-colors justify-center"
      >
        <LogOut className="h-3.5 w-3.5" /> {t('common.signOut')}
      </button>
    </div>
  );
}

function PaymentsTab({
  payments,
  loading,
  error,
}: Readonly<{
  payments: PaymentRecord[];
  loading: boolean;
  error: string | null;
}>) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!user) {
    return (
      <div className="text-center py-6">
        <p className="text-xs text-slate-500 mb-3">{t('payments.signInPrompt')}</p>
        <button
          onClick={() => navigate('/login')}
          className="text-xs text-indigo-600 font-semibold hover:underline"
        >
          {t('common.signIn')}
        </button>
      </div>
    );
  }

  if (loading) {
    return <div className="text-xs text-slate-400 text-center py-6">{t('common.loading')}</div>;
  }

  if (error) {
    return <div className="text-xs text-rose-500 text-center py-6">{error}</div>;
  }

  if (payments.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-700 mb-3 mx-auto">
          <Receipt className="h-5 w-5 text-slate-500" />
        </div>
        <p className="text-sm font-semibold text-slate-400">{t('payments.emptyTitle')}</p>
        <p className="text-xs text-slate-500 mt-1">{t('payments.emptyBody')}</p>
        <button
          onClick={() => navigate(PRICING_PATH)}
          className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-xl bg-violet-600 px-4 py-2 text-xs font-semibold text-white hover:bg-violet-500 transition-colors"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          {t('payments.viewPlans')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {payments.map((p) => (
        <div
          key={p.id}
          className="rounded-xl border border-slate-700 bg-slate-800 p-3 flex items-center justify-between gap-2"
        >
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-white truncate">
              {t('subscription.planLabel', { plan: t(PLAN_NAME_KEYS[p.plan]) })}
            </p>
            <p className="text-xs text-slate-500 truncate">{formatDate(p.createdAt)}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-xs font-bold text-white">৳{p.amount}</span>
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${getPaymentStatusColor(p.status)}`}
            >
              {t(`status.${p.status}`)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function LeaderboardTab({ leaderboard }: Readonly<Pick<PanelTabContentProps, 'leaderboard'>>) {
  return (
    <div className="space-y-0.5">
      {leaderboard.map((entry) => {
        const medalColor = getMedalColor(entry.rank);
        return (
          <div
            key={entry.rank}
            className={`flex items-center gap-2 px-3 py-2.5 rounded-xl transition-colors ${entry.isCurrentUser ? 'bg-violet-500/10 border border-violet-500/30' : 'hover:bg-slate-700'}`}
          >
            <div className="w-6 flex-shrink-0 text-center">
              {entry.rank <= 3 ? (
                <Medal className={`h-4 w-4 mx-auto ${medalColor}`} />
              ) : (
                <span className="text-xs font-bold text-slate-400">#{entry.rank}</span>
              )}
            </div>
            <span
              className={`flex-1 text-xs font-medium truncate ${entry.isCurrentUser ? 'text-violet-300' : 'text-slate-300'}`}
            >
              {entry.name}
            </span>
            <span
              className={`text-xs font-bold flex-shrink-0 ${entry.isCurrentUser ? 'text-violet-400' : 'text-slate-200'}`}
            >
              {formatNumber(entry.totalQuestions)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function PanelTabContent(props: Readonly<PanelTabContentProps>) {
  const { activeTab } = props;
  if (activeTab === 'overview') {
    return (
      <OverviewTab
        analyticsRows={props.analyticsRows}
        leaderboard={props.leaderboard}
        myRank={props.myRank}
        totalQuestions={props.totalQuestions}
        setActiveTab={props.setActiveTab}
      />
    );
  }
  if (activeTab === 'history') {
    return (
      <HistoryTab
        pagedHistory={props.pagedHistory}
        historyPage={props.historyPage}
        totalPages={props.totalPages}
        setHistoryPage={props.setHistoryPage}
        setViewingEntry={props.setViewingEntry}
      />
    );
  }
  if (activeTab === 'subscription') {
    return (
      <SubscriptionTab
        sub={props.sub}
        loading={props.subLoading}
        error={props.subError}
        onCancelSuccess={props.onCancelSuccess}
      />
    );
  }
  if (activeTab === 'payments') {
    return (
      <PaymentsTab payments={props.payments} loading={props.subLoading} error={props.subError} />
    );
  }
  if (activeTab === 'leaderboard') {
    return <LeaderboardTab leaderboard={props.leaderboard} />;
  }
  if (activeTab === 'admin-users') {
    return (
      <AdminUsersTab
        onViewActivity={(userId) => {
          props.setAdminUserFilter(userId);
          props.setActiveTab('admin-activity');
        }}
      />
    );
  }
  if (activeTab === 'admin-activity') {
    return (
      <AdminActivityTab
        userIdFilter={props.adminUserFilter}
        onClearUserFilter={() => props.setAdminUserFilter(undefined)}
      />
    );
  }
  return <AdminStatsTab />;
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const MIN_PANEL = 260;
const MAX_PANEL = 560;
const DEFAULT_PANEL = 320;
const PAGE_SIZE = 6;

export function DashboardPage() {
  const { signOut, isSuperAdmin } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showWizard, setShowWizard] = useState(false);
  // Set by clicking a row in the Users tab, which then jumps to Activity.
  const [adminUserFilter, setAdminUserFilter] = useState<string | undefined>(undefined);
  const [generatedPaper, setGeneratedPaper] = useState<GeneratedExamPaper | null>(null);

  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL);
  const [panelOpen, setPanelOpen] = useState(true);

  const startDragging = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = panelWidth;
      const onMove = (mv: MouseEvent) => {
        setPanelWidth(Math.min(MAX_PANEL, Math.max(MIN_PANEL, startWidth + mv.clientX - startX)));
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [panelWidth]
  );

  const {
    history,
    subscription,
    payments,
    subLoading,
    subError,
    refreshSubscription,
    daysRemaining,
    questionsThisMonth,
    totalQuestions,
    analyticsRows,
    leaderboard,
    myRank,
    addEntry,
  } = useDashboardData();

  const renewalReminder = useRenewalReminder(subscription);

  // The URL owns the active tab, so it is derived rather than mirrored in state.
  const { pathname } = useLocation();
  const activeTab = resolveDashboardTab(pathname);
  const setActiveTab = useCallback((tab: Tab) => navigate(getDashboardTabPath(tab)), [navigate]);

  const navGroups = useMemo(
    () => (isSuperAdmin ? [...NAV_GROUPS, ADMIN_NAV_GROUP] : NAV_GROUPS),
    [isSuperAdmin]
  );

  const [historyPage, setHistoryPage] = useState(1);
  const [viewingEntry, setViewingEntry] = useState<HistoryEntry | null>(null);

  const totalPages = Math.max(1, Math.ceil(history.length / PAGE_SIZE));
  const pagedHistory = history.slice((historyPage - 1) * PAGE_SIZE, historyPage * PAGE_SIZE);

  // ─── Overview panel content (wizard / result / dashboard grid) ─────────────
  let overviewContent: React.ReactNode;
  if (showWizard) {
    overviewContent = (
      <WizardEmbed
        onClose={() => setShowWizard(false)}
        onGenerated={(paper) => {
          addEntry(paper);
          setGeneratedPaper(paper);
          setShowWizard(false);
        }}
      />
    );
  } else if (generatedPaper) {
    overviewContent = (
      <GeneratedQuestionsEmbed examPaper={generatedPaper} onClose={() => setGeneratedPaper(null)} />
    );
  }

  // Every tab gets a URL for free, so a non-admin can type /admin-users. The API
  // is the real boundary — this just sends them somewhere that renders, instead
  // of an empty page whose requests 403 behind it.
  if (isAdminTab(activeTab) && !isSuperAdmin) {
    return <Navigate to={getDashboardTabPath(DEFAULT_DASHBOARD_TAB)} replace />;
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-900">
      {/* ══ LEFT SIDEBAR (Sneat-style) ══════════════════════════════════════ */}
      {panelOpen && (
        <aside
          style={{ width: panelWidth }}
          className="flex-shrink-0 flex flex-col h-full bg-slate-800 border-r border-slate-700 overflow-hidden z-10"
        >
          {/* Logo bar */}
          <div className="flex items-center gap-2.5 px-5 h-16 border-b border-slate-700 flex-shrink-0">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600 shadow-[0_4px_10px_rgba(99,102,241,0.35)] flex-shrink-0">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <span className="text-[15px] font-extrabold text-white tracking-tight">
              {t('common.brand')}
            </span>
            <div className="flex-1" />
            <button
              onClick={() => setPanelOpen(false)}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-all"
              title={t('nav.collapse')}
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </div>

          {/* Navigation with category groups */}
          <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
            {navGroups.map((group) => (
              <div key={group.labelKey}>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.12em] px-3 mb-2">
                  {t(group.labelKey)}
                </p>
                <div className="space-y-0.5">
                  {group.tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-150 text-left ${
                          isActive
                            ? 'bg-violet-500/20 text-violet-300 font-semibold'
                            : 'text-slate-400 hover:bg-slate-700/50 hover:text-slate-200 font-medium'
                        }`}
                      >
                        <Icon
                          className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-violet-400' : 'text-slate-500'}`}
                        />
                        {t(tab.labelKey)}
                        {isActive && (
                          <span className="ml-auto h-2 w-2 rounded-full bg-violet-400 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Footer: plan strip + sign out */}
          <div className="flex-shrink-0 border-t border-slate-700 px-4 py-3 space-y-2.5 bg-slate-900/60">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-violet-600 flex-shrink-0">
                <CreditCard className="h-3.5 w-3.5 text-white" />
              </div>
              <span className="text-xs font-semibold text-slate-300 flex-1 truncate">
                {t('subscription.planLabel', {
                  plan: subscription
                    ? t(PLAN_NAME_KEYS[subscription.plan])
                    : t('common.emptyValue'),
                })}
              </span>
              <span
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                  daysRemaining <= 5
                    ? 'bg-rose-500/20 text-rose-400'
                    : 'bg-emerald-500/20 text-emerald-400'
                }`}
              >
                {daysRemaining === 0
                  ? t('common.expired')
                  : t('subscription.daysShort', { days: daysRemaining })}
              </span>
            </div>
            <button
              onClick={() => signOut()}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-400 hover:bg-rose-500/10 hover:text-rose-400 transition-all duration-150"
            >
              <LogOut className="h-4 w-4 flex-shrink-0" />
              {t('common.signOut')}
            </button>
          </div>
        </aside>
      )}

      {/* ══ DRAG HANDLE ═════════════════════════════════════════════════════ */}
      {panelOpen && (
        <button
          type="button"
          aria-label={t('nav.resize')}
          className="flex-shrink-0 w-1.5 bg-slate-700/50 hover:bg-violet-500 cursor-col-resize transition-colors group flex items-center justify-center z-10 border-0 p-0"
          onMouseDown={startDragging}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') setPanelWidth((w) => Math.max(MIN_PANEL, w - 20));
            if (e.key === 'ArrowRight') setPanelWidth((w) => Math.min(MAX_PANEL, w + 20));
          }}
          title={t('nav.dragResize')}
        >
          <GripVertical className="h-4 w-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
      )}

      {/* ══ MAIN CONTENT ════════════════════════════════════════════════════ */}
      <main className="flex-1 overflow-hidden flex flex-col min-w-0">
        {/* Top header bar */}
        <header className="flex-shrink-0 flex items-center gap-4 px-6 h-16 bg-slate-800 border-b border-slate-700">
          {!panelOpen && (
            <button
              onClick={() => setPanelOpen(true)}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:text-violet-400 hover:bg-slate-700 transition-all"
            >
              <PanelLeftOpen className="h-4 w-4" />
            </button>
          )}
          <div className="flex-1">
            <p className="text-[10px] text-slate-500 leading-none">{t('nav.breadcrumb')}</p>
            <h1 className="text-sm font-bold text-white mt-0.5">
              {(() => {
                const tab = ALL_TABS.find((entry) => entry.id === activeTab);
                return tab ? t(tab.labelKey) : t('common.dashboard');
              })()}
            </h1>
          </div>
          {/* Occupies the slot the "New Generation" button used to hold. Generation is
              still reachable from the hero card's Start Generating button below. */}
          <LanguageToggle variant="segmented" />
        </header>

        {/* Sits between the header and the scroll container so it stays put on every
            tab: an expired account cannot generate anything, so this is worth the
            interruption until it is dismissed or acted on. */}
        {renewalReminder.visible && <RenewalReminderBanner onDismiss={renewalReminder.dismiss} />}

        {/* Scrollable page body */}
        <div className="flex-1 overflow-auto p-6">
          {/* ─── OVERVIEW ──────────────────────────────────────────────────── */}
          {activeTab === 'overview' && (
            <div className="space-y-5 max-w-[1280px]">
              {overviewContent ?? (
                <>
                  {/* Row 1: Welcome card + 2 stat tiles */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    <div className="lg:col-span-2 bg-slate-800 rounded-xl p-6 border border-slate-700 shadow-sm relative overflow-hidden">
                      <div className="flex items-start gap-4 relative z-10">
                        <div className="flex-1">
                          <p className="text-xs font-bold text-violet-400 uppercase tracking-wide mb-1.5">
                            {t('common.appName')}
                          </p>
                          <h2 className="text-2xl font-black text-white leading-snug mb-2">
                            {t('dashboard.heroTitle')}
                          </h2>
                          <p className="text-sm text-slate-400 leading-relaxed mb-5 max-w-xs">
                            {t('dashboard.heroBody')}
                          </p>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => setShowWizard(true)}
                            className="gap-1.5"
                          >
                            <Sparkles className="h-3.5 w-3.5" />
                            {t('dashboard.heroCta')}
                          </Button>
                        </div>
                        <div className="hidden sm:flex h-24 w-24 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-700 to-slate-700 border border-slate-600 flex-shrink-0 mt-1 select-none">
                          <span className="text-5xl">📚</span>
                        </div>
                      </div>
                      <div className="absolute -right-8 -bottom-8 h-32 w-32 rounded-full bg-gradient-to-br from-brand-100 to-violet-100 opacity-60 blur-2xl pointer-events-none" />
                    </div>

                    {/* Right: stat tiles */}
                    <div className="space-y-5">
                      <QuickStatCard
                        icon={BookOpen}
                        label={t('dashboard.statTotalQuestions')}
                        value={formatNumber(totalQuestions)}
                        sub={t('dashboard.statThisMonth', {
                          count: formatNumber(questionsThisMonth),
                        })}
                        gradient="from-sky-400 to-blue-600"
                      />
                    </div>
                  </div>

                  {/* Row 2: Analytics table + rank tile + mini leaderboard */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    {/* Analytics table */}
                    <div className="lg:col-span-2 bg-slate-800 rounded-xl border border-slate-700 shadow-sm overflow-hidden">
                      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700">
                        <div className="flex items-center gap-2">
                          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-500/10">
                            <BarChart3 className="h-4 w-4 text-violet-400" />
                          </div>
                          <h3 className="text-sm font-bold text-white">
                            {t('dashboard.analyticsTitle')}
                          </h3>
                        </div>
                        <span className="text-xs font-semibold text-slate-400 bg-slate-700 px-2.5 py-1 rounded-full">
                          {t('dashboard.analyticsCount', { count: analyticsRows.length })}
                        </span>
                      </div>
                      {analyticsRows.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-14 text-center">
                          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-700 mb-3">
                            <BarChart3 className="h-5 w-5 text-slate-500" />
                          </div>
                          <p className="text-sm font-semibold text-slate-400">
                            {t('dashboard.analyticsEmptyTitle')}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {t('dashboard.analyticsEmptyBody')}
                          </p>
                        </div>
                      ) : (
                        <div className="divide-y divide-slate-700">
                          {analyticsRows.map((row) => {
                            const rowKey = `${row.subject}__${row.department}__${row.classLevel}`;
                            return (
                              <div
                                key={rowKey}
                                className="flex items-center gap-4 px-5 py-3 hover:bg-slate-700/50 transition-colors"
                              >
                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-500/10 flex-shrink-0">
                                  <BookOpen className="h-3.5 w-3.5 text-violet-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-white truncate">
                                    {row.subject}
                                  </p>
                                  <p className="text-xs text-slate-400 truncate">
                                    {departmentLabel(row.department, t)} ·{' '}
                                    {levelLabel(row.classLevel)}
                                  </p>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <p className="text-sm font-black text-violet-400">
                                    {formatNumber(row.count)}
                                  </p>
                                  <p className="text-[10px] text-slate-400">
                                    {t('common.questions')}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Right: rank tile + mini leaderboard */}
                    <div className="space-y-5">
                      <QuickStatCard
                        icon={Trophy}
                        label={t('dashboard.statRank')}
                        value={`#${myRank}`}
                        sub={t('dashboard.statRankSub', { count: leaderboard.length })}
                        gradient="from-amber-400 to-yellow-500"
                      />
                      <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-sm overflow-hidden">
                        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700">
                          <Trophy className="h-4 w-4 text-amber-500" />
                          <h3 className="text-xs font-bold text-slate-300">
                            {t('dashboard.topUsers')}
                          </h3>
                          <button
                            onClick={() => setActiveTab('leaderboard')}
                            className="ml-auto text-[10px] font-semibold text-violet-400 hover:text-violet-300 transition-colors"
                          >
                            {t('dashboard.seeAll')}
                          </button>
                        </div>
                        <div className="divide-y divide-slate-700">
                          {leaderboard.slice(0, 4).map((entry) => (
                            <div
                              key={entry.rank}
                              className={`flex items-center gap-2.5 px-4 py-2.5 ${
                                entry.isCurrentUser ? 'bg-violet-500/10' : ''
                              }`}
                            >
                              <div className="w-5 flex-shrink-0 text-center">
                                {entry.rank <= 3 ? (
                                  <Medal
                                    className={`h-3.5 w-3.5 mx-auto ${getMedalColor(entry.rank)}`}
                                  />
                                ) : (
                                  <span className="text-xs font-bold text-slate-500">
                                    {entry.rank}
                                  </span>
                                )}
                              </div>
                              <span
                                className={`flex-1 text-xs font-medium truncate ${
                                  entry.isCurrentUser ? 'text-violet-300' : 'text-slate-300'
                                }`}
                              >
                                {entry.name}
                              </span>
                              <span
                                className={`text-xs font-black flex-shrink-0 ${
                                  entry.isCurrentUser ? 'text-violet-400' : 'text-slate-200'
                                }`}
                              >
                                {formatNumber(entry.totalQuestions)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Row 3: Recent history table */}
                  {history.length > 0 && (
                    <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-sm overflow-hidden">
                      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700">
                        <div className="flex items-center gap-2">
                          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-500/10">
                            <BookOpen className="h-4 w-4 text-violet-500" />
                          </div>
                          <h3 className="text-sm font-bold text-white">
                            {t('dashboard.recentTitle')}
                          </h3>
                        </div>
                        <button
                          onClick={() => setActiveTab('history')}
                          className="text-xs font-semibold text-violet-400 hover:text-violet-300 transition-colors"
                        >
                          {t('dashboard.recentViewAll', { count: history.length })}
                        </button>
                      </div>
                      <div className="divide-y divide-slate-700">
                        {history.slice(0, 5).map((entry) => (
                          <div
                            key={entry.id}
                            className="flex items-center gap-4 px-5 py-3 hover:bg-slate-700/50 transition-colors"
                          >
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-500/10 flex-shrink-0">
                              <BookOpen className="h-3.5 w-3.5 text-violet-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-white truncate">
                                {entry.subject}
                              </p>
                              <p className="text-xs text-slate-400 truncate">
                                {levelLabel(entry.classLevel)} · {entry.examType} ·{' '}
                                {entry.difficulty} ·{' '}
                                {t('dashboard.questionsCount', {
                                  count: entry.numberOfQuestions,
                                })}
                              </p>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <span className="hidden sm:block text-xs text-slate-500">
                                {formatDate(entry.generatedAt)}
                              </span>
                              <button
                                onClick={() => setViewingEntry(entry)}
                                className="flex items-center gap-1 text-xs font-semibold text-violet-400 hover:text-violet-300 transition-colors"
                              >
                                <Eye className="h-3 w-3" />
                                {t('common.view')}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ─── ALL OTHER TABS ────────────────────────────────────────────── */}
          {activeTab !== 'overview' &&
            (() => {
              const content = (
                <PanelTabContent
                  activeTab={activeTab}
                  history={history}
                  pagedHistory={pagedHistory}
                  historyPage={historyPage}
                  totalPages={totalPages}
                  analyticsRows={analyticsRows}
                  leaderboard={leaderboard}
                  myRank={myRank}
                  totalQuestions={totalQuestions}
                  sub={subscription}
                  subLoading={subLoading}
                  subError={subError}
                  payments={payments}
                  daysRemaining={daysRemaining}
                  setHistoryPage={setHistoryPage}
                  setViewingEntry={setViewingEntry}
                  setActiveTab={setActiveTab}
                  onCancelSuccess={refreshSubscription}
                  adminUserFilter={adminUserFilter}
                  setAdminUserFilter={setAdminUserFilter}
                />
              );

              // Admin tabs bring their own panel chrome and hold wide tables, so
              // they render bare and full width. The account tabs keep the
              // narrow titled card they were designed for.
              if (isAdminTab(activeTab)) {
                return <div className="max-w-[1280px]">{content}</div>;
              }

              return (
                <div className="max-w-2xl">
                  <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-sm overflow-hidden">
                    <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-700">
                      {(() => {
                        const tab = ALL_TABS.find((entry) => entry.id === activeTab);
                        if (!tab) return null;
                        const Icon = tab.icon;
                        return (
                          <>
                            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-500/10">
                              <Icon className="h-3.5 w-3.5 text-violet-400" />
                            </div>
                            <h3 className="text-sm font-bold text-white">{t(tab.labelKey)}</h3>
                          </>
                        );
                      })()}
                    </div>
                    <div className="p-4">{content}</div>
                  </div>
                </div>
              );
            })()}
        </div>
      </main>

      {/* ── History Viewer Dialog ─────────────────────────────────────────── */}
      <Dialog
        open={!!viewingEntry}
        onOpenChange={(open) => {
          if (!open) setViewingEntry(null);
        }}
      >
        <DialogContent className="max-w-4xl w-full">
          <DialogHeader>
            <DialogTitle>
              {viewingEntry
                ? (() => {
                    const examTypeLabel =
                      viewingEntry.examType === 'written' ? t('common.written') : t('common.mcq');
                    return `${viewingEntry.subject} \u2014 ${levelLabel(viewingEntry.classLevel)} ${examTypeLabel}`;
                  })()
                : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[70vh] mt-2">
            {viewingEntry && <A4Paper paper={viewingEntry.examPaper} />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
